import { useEffect, useRef, useState } from 'react';

interface UseOtpTimerOptions {
  /** Seconds to count down before the resend button re-enables. Default: 30 */
  cooldown?: number;
}

interface UseOtpTimerReturn {
  secondsLeft: number;
  isActive: boolean;
  start: () => void;
  reset: () => void;
}

/**
 * Manages the OTP resend cooldown timer.
 *
 * Call `start()` after a successful OTP dispatch to begin the countdown.
 * While `isActive` is true the resend button should be disabled.
 * The interval is cleared automatically on unmount.
 */
export function useOtpTimer({ cooldown = 30 }: UseOtpTimerOptions = {}): UseOtpTimerReturn {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function clearTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  function start() {
    clearTimer();
    setSecondsLeft(cooldown);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function reset() {
    clearTimer();
    setSecondsLeft(0);
  }

  // Safety: clear on unmount
  useEffect(() => () => clearTimer(), []);

  return { secondsLeft, isActive: secondsLeft > 0, start, reset };
}
